import { useState, useEffect } from 'react';
import {
  QrCode, Download, Copy, Check, ExternalLink,
  Loader2, RefreshCw, Star, MessageSquare, Camera, Calendar, Users,
  Share2, Facebook, Phone, Package, Printer, Edit2, UserPlus
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import adminApi from '../../services/adminApi';

import { PageTitle } from './shared';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icons for each QR code type
const QR_TYPE_ICONS = {
  google_review: Star,
  feedback: MessageSquare,
  photo_upload: Camera,
  event_registration: Calendar,
  member_portal: Users,
  new_member_registration: UserPlus,
  existing_customer_add_student: UserPlus,
  share_experience: Share2,
  facebook: Facebook,
  vcard: Phone,
  resource_link: Package,
};

// Print card configuration for each QR code type
const PRINT_CARD_CONFIG = {
  google_review: {
    headline: 'Love Training Here?',
    cta: 'Scan to leave us a review!',
    bgColor: 'bg-yellow-50',
    accentColor: 'border-yellow-400',
    textColor: 'text-yellow-800',
  },
  feedback: {
    headline: 'Help Us Improve',
    cta: 'Scan to share your feedback',
    bgColor: 'bg-blue-50',
    accentColor: 'border-blue-400',
    textColor: 'text-blue-800',
  },
  photo_upload: {
    headline: 'Captured a Great Moment?',
    cta: 'Scan to share your photos!',
    bgColor: 'bg-green-50',
    accentColor: 'border-green-400',
    textColor: 'text-green-800',
  },
  event_registration: {
    headline: "What's Happening at Owaza",
    cta: 'Scan for upcoming events',
    bgColor: 'bg-purple-50',
    accentColor: 'border-purple-400',
    textColor: 'text-purple-800',
  },
  member_portal: {
    headline: 'Student Portal',
    cta: 'Scan to access your account',
    bgColor: 'bg-indigo-50',
    accentColor: 'border-indigo-400',
    textColor: 'text-indigo-800',
  },
  new_member_registration: {
    headline: 'Join Owaza Today!',
    cta: 'Scan to register as a new member',
    bgColor: 'bg-emerald-50',
    accentColor: 'border-emerald-400',
    textColor: 'text-emerald-800',
  },
  existing_customer_add_student: {
    headline: 'Add a Student',
    cta: 'Existing customers: add to your account',
    bgColor: 'bg-teal-50',
    accentColor: 'border-teal-400',
    textColor: 'text-teal-800',
  },
  share_experience: {
    headline: 'Proud of Your Progress?',
    cta: 'Scan to share your experience!',
    bgColor: 'bg-pink-50',
    accentColor: 'border-pink-400',
    textColor: 'text-pink-800',
  },
  facebook: {
    headline: 'Stay Connected',
    cta: 'Follow us on Facebook!',
    bgColor: 'bg-blue-50',
    accentColor: 'border-blue-600',
    textColor: 'text-blue-900',
  },
  vcard: {
    headline: 'Save Our Contact',
    cta: 'Scan to add us to your contacts',
    bgColor: 'bg-gray-50',
    accentColor: 'border-gray-400',
    textColor: 'text-gray-800',
  },
  resource_link: {
    headline: 'Learning Resource',
    cta: 'Scan to access this resource',
    bgColor: 'bg-indigo-50',
    accentColor: 'border-indigo-400',
    textColor: 'text-indigo-800',
  },
};

// Descriptions for each QR code type
const QR_TYPE_INFO = {
  google_review: {
    name: 'Google Review',
    description: 'Links to Google Review page',
    color: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300',
  },
  feedback: {
    name: 'Feedback Survey',
    description: 'Internal feedback collection form',
    color: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300',
  },
  photo_upload: {
    name: 'Photo Upload',
    description: 'Customer photo submissions',
    color: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300',
  },
  event_registration: {
    name: 'Events Page',
    description: 'Upcoming events and registration',
    color: 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300',
  },
  member_portal: {
    name: 'Member Portal',
    description: 'Student login portal',
    color: 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300',
  },
  new_member_registration: {
    name: 'New Member Registration',
    description: 'Prospective member sign-up form',
    color: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300',
  },
  existing_customer_add_student: {
    name: 'Add Student (Existing)',
    description: 'Existing customers add student',
    color: 'bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300',
  },
  share_experience: {
    name: 'Share Experience',
    description: 'Social sharing page',
    color: 'bg-pink-100 dark:bg-pink-900/50 text-pink-700 dark:text-pink-300',
  },
  facebook: {
    name: 'Facebook Page',
    description: 'Follow us on Facebook',
    color: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300',
  },
  vcard: {
    name: 'Contact Card',
    description: 'Save our contact info',
    color: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  },
  resource_link: {
    name: 'Resource Link',
    description: 'Deep link to learning content',
    color: 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300',
  },
};

// Type-specific decorative icons for cards
const TYPE_DECORATIONS = {
  google_review: { icon: '⭐', showStars: true },
  feedback: { icon: '💬', showStars: false },
  photo_upload: { icon: '📸', showStars: false },
  event_registration: { icon: '📅', showStars: false },
  member_portal: { icon: '🥋', showStars: false },
  new_member_registration: { icon: '✨', showStars: false },
  share_experience: { icon: '🎉', showStars: false },
  facebook: { icon: 'f', isFacebook: true },
  vcard: { icon: '📇', showStars: false },
  resource_link: { icon: '🔗', showStars: false },
};

// Printable card component
const PrintableCard = ({ qrCode, qrSvg, size }) => {
  const config = PRINT_CARD_CONFIG[qrCode.type] || {
    headline: qrCode.title || 'Scan Me',
    cta: 'Scan this QR code',
    bgColor: 'bg-gray-50',
    accentColor: 'border-gray-400',
    textColor: 'text-gray-800',
  };

  const decoration = TYPE_DECORATIONS[qrCode.type] || {};
  const isBusinessCard = size === 'business';

  // Size classes for the card
  const sizeClasses = isBusinessCard
    ? 'w-[3.5in] h-[2in]'
    : 'w-[5.5in] h-[4.25in]';

  const qrSize = isBusinessCard ? 'w-20 h-20' : 'w-40 h-40';
  const headlineSize = isBusinessCard ? 'text-lg' : 'text-2xl';
  const ctaSize = isBusinessCard ? 'text-xs' : 'text-base';
  const logoSize = isBusinessCard ? 'h-6' : 'h-10';
  const padding = isBusinessCard ? 'p-3' : 'p-6';
  const starSize = isBusinessCard ? 'text-sm' : 'text-xl';

  return (
    <div
      className={`${sizeClasses} ${config.bgColor} ${padding} border-4 ${config.accentColor} rounded-lg flex flex-col items-center justify-between print-card relative overflow-hidden`}
    >
      {/* Logo */}
      <div className="flex items-center gap-2">
        <img
          src="/logo192.png"
          alt="Owaza"
          className={`${logoSize} object-contain`}
        />
        <span className={`font-bold ${config.textColor} ${isBusinessCard ? 'text-sm' : 'text-lg'}`}>
          Owaza Martial Arts
        </span>
      </div>

      {/* Headline with optional stars */}
      <div className="flex items-center gap-2">
        {decoration.showStars && <span className={starSize}>⭐</span>}
        <h2 className={`${headlineSize} font-bold ${config.textColor} text-center`}>
          {config.headline}
        </h2>
        {decoration.showStars && <span className={starSize}>⭐</span>}
      </div>

      {/* QR Code - with proper SVG containment */}
      <div
        className={`${qrSize} bg-background p-2 rounded-lg shadow-sm border border-gray-200 overflow-hidden flex items-center justify-center [&>svg]:w-full [&>svg]:h-full [&>svg]:max-w-full [&>svg]:max-h-full`}
        dangerouslySetInnerHTML={{ __html: qrSvg }}
      />

      {/* CTA */}
      <p className={`${ctaSize} ${config.textColor} text-center font-medium`}>
        {config.cta}
      </p>
    </div>
  );
};

// Print card dialog component
const PrintCardDialog = ({ qrCode, open, onClose }) => {
  const [size, setSize] = useState('business');
  const [includeLogo, setIncludeLogo] = useState(true);
  const [qrSvg, setQrSvg] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch QR code SVG when dialog opens or logo toggle changes
  useEffect(() => {
    if (!open || !qrCode) return;

    const fetchQrSvg = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('googleAuthToken');
        const response = await fetch(
          `${API}/admin/qr-codes/${qrCode.type}/download?size=300&include_logo=${includeLogo}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) throw new Error('Failed to fetch QR code');
        const svgText = await response.text();
        setQrSvg(svgText);
      } catch (err) {
        console.error('Failed to fetch QR code:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchQrSvg();
  }, [open, qrCode, includeLogo]);

  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const config = PRINT_CARD_CONFIG[qrCode.type] || {};
    const decoration = TYPE_DECORATIONS[qrCode.type] || {};
    const isBusinessCard = size === 'business';

    // Build inline styles for the print window
    const cardWidth = isBusinessCard ? '3.5in' : '5.5in';
    const cardHeight = isBusinessCard ? '2in' : '4.25in';
    const qrSize = isBusinessCard ? '80px' : '160px';
    const headlineFontSize = isBusinessCard ? '18px' : '24px';
    const ctaFontSize = isBusinessCard ? '12px' : '16px';
    const logoHeight = isBusinessCard ? '24px' : '40px';
    const padding = isBusinessCard ? '12px' : '24px';
    const starFontSize = isBusinessCard ? '14px' : '20px';

    // Map Tailwind colors to CSS
    const colorMap = {
      'bg-yellow-50': '#fefce8',
      'bg-blue-50': '#eff6ff',
      'bg-green-50': '#f0fdf4',
      'bg-purple-50': '#faf5ff',
      'bg-indigo-50': '#eef2ff',
      'bg-pink-50': '#fdf2f8',
      'bg-gray-50': '#f9fafb',
      'border-yellow-400': '#facc15',
      'border-blue-400': '#60a5fa',
      'border-blue-600': '#2563eb',
      'border-green-400': '#4ade80',
      'border-purple-400': '#c084fc',
      'border-indigo-400': '#818cf8',
      'border-pink-400': '#f472b6',
      'border-gray-400': '#9ca3af',
      'text-yellow-800': '#854d0e',
      'text-blue-800': '#1e40af',
      'text-blue-900': '#1e3a8a',
      'text-green-800': '#166534',
      'text-purple-800': '#6b21a8',
      'text-indigo-800': '#3730a3',
      'text-pink-800': '#9d174d',
      'text-gray-800': '#1f2937',
    };

    const bgColor = colorMap[config.bgColor] || '#f9fafb';
    const borderColor = colorMap[config.accentColor] || '#9ca3af';
    const textColor = colorMap[config.textColor] || '#1f2937';

    // Stars decoration for review card
    const starsHtml = decoration.showStars
      ? `<span class="star">⭐</span>`
      : '';

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print QR Code - ${qrCode.title || config.headline}</title>
        <style>
          @page {
            size: ${cardWidth} ${cardHeight};
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: white;
          }
          .print-card {
            width: ${cardWidth};
            height: ${cardHeight};
            background-color: ${bgColor};
            border: 4px solid ${borderColor};
            border-radius: 8px;
            padding: ${padding};
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: space-between;
            font-family: system-ui, -apple-system, sans-serif;
            overflow: hidden;
          }
          .logo-row {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .logo-row img {
            height: ${logoHeight};
            object-fit: contain;
          }
          .logo-row span {
            font-weight: bold;
            color: ${textColor};
            font-size: ${isBusinessCard ? '14px' : '18px'};
          }
          .headline-row {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .headline {
            font-size: ${headlineFontSize};
            font-weight: bold;
            color: ${textColor};
            text-align: center;
            margin: 0;
          }
          .star {
            font-size: ${starFontSize};
          }
          .qr-container {
            width: ${qrSize};
            height: ${qrSize};
            background: white;
            padding: 8px;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }
          .qr-container svg {
            width: 100%;
            height: 100%;
            max-width: 100%;
            max-height: 100%;
            display: block;
          }
          .cta {
            font-size: ${ctaFontSize};
            color: ${textColor};
            text-align: center;
            font-weight: 500;
            margin: 0;
          }
        </style>
      </head>
      <body>
        <div class="print-card">
          <div class="logo-row">
            <img src="${window.location.origin}/logo192.png" alt="Owaza" />
            <span>Owaza Martial Arts</span>
          </div>
          <div class="headline-row">
            ${starsHtml}
            <h2 class="headline">${config.headline || 'Scan Me'}</h2>
            ${starsHtml}
          </div>
          <div class="qr-container">
            ${qrSvg}
          </div>
          <p class="cta">${config.cta || 'Scan this QR code'}</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();

    // Wait for content to load, then print
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      // Close after a delay to allow print dialog
      setTimeout(() => printWindow.close(), 1000);
    };
  };

  if (!qrCode) return null;

  const config = PRINT_CARD_CONFIG[qrCode.type] || {};

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Print QR Code Card</DialogTitle>
          <DialogDescription>
            Preview and print a styled card for "{qrCode.title || config.headline}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Size selector */}
          <div className="flex items-center gap-4">
            <Label>Card Size:</Label>
            <div className="flex gap-2">
              <Button
                variant={size === 'business' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSize('business')}
              >
                Business Card (3.5" x 2")
              </Button>
              <Button
                variant={size === 'half' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSize('half')}
              >
                Half Page (5.5" x 4.25")
              </Button>
            </div>
          </div>

          {/* Logo toggle */}
          <div className="flex items-center gap-3">
            <Switch
              id="include-logo"
              checked={includeLogo}
              onCheckedChange={setIncludeLogo}
            />
            <Label htmlFor="include-logo" className="cursor-pointer">
              Include Owaza logo in QR code center
            </Label>
          </div>

          {/* Preview */}
          <div className="flex justify-center p-4 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
              </div>
            ) : (
              <div className="transform scale-90 origin-center">
                <PrintableCard qrCode={qrCode} qrSvg={qrSvg} size={size} />
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handlePrint}
            disabled={loading || !qrSvg}
            variant="adminGold"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const QRCodeCard = ({ qrCode, onToggle, onDownload, onEdit, onPrint, downloadingId }) => {
  const [copied, setCopied] = useState(false);
  const Icon = QR_TYPE_ICONS[qrCode.type] || QrCode;
  const info = QR_TYPE_INFO[qrCode.type] || { name: qrCode.type, description: '', color: 'bg-gray-100 dark:bg-gray-700' };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(qrCode.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Card className={`transition-all ${!qrCode.enabled ? 'opacity-60' : ''}`}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${info.color}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">{qrCode.title || info.name}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">{info.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Switch
              checked={qrCode.enabled}
              onCheckedChange={() => onToggle(qrCode)}
              className="data-[state=checked]:bg-green-500"
            />
          </div>
        </div>

        {/* URL Display */}
        <div className="mb-3 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Target URL</p>
          <p className="text-sm text-gray-700 dark:text-gray-300 truncate font-mono">{qrCode.url}</p>
          {qrCode.custom_url && (
            <p className="text-xs text-amber-600 mt-1">Custom URL set</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDownload(qrCode)}
            disabled={!qrCode.enabled || downloadingId === qrCode.type}
            className="flex-1"
            title="Download QR code"
          >
            {downloadingId === qrCode.type ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPrint(qrCode)}
            disabled={!qrCode.enabled}
            title="Print card"
          >
            <Printer className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            disabled={!qrCode.enabled}
            title="Copy URL"
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-600" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(qrCode.url, '_blank')}
            disabled={!qrCode.enabled}
            title="Open URL"
          >
            <ExternalLink className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(qrCode)}
            title="Edit QR code"
          >
            <Edit2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const QRCodeCenter = () => {
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [editingQr, setEditingQr] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [printingQr, setPrintingQr] = useState(null);

  const loadQrCodes = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/api/admin/qr-codes');
      if (!response.ok) throw new Error('Failed to load QR codes');
      const data = await response.json();
      setQrCodes(data);
      setError(null);
    } catch (err) {
      setError('Failed to load QR codes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQrCodes();
  }, []);

  const handleToggle = async (qrCode) => {
    try {
      const response = await adminApi.put(`/api/admin/qr-codes/${qrCode.type}`, {
        enabled: !qrCode.enabled,
      });
      if (!response.ok) throw new Error('Failed to toggle');
      await loadQrCodes();
    } catch (err) {
      console.error('Failed to toggle QR code:', err);
    }
  };

  const handleDownload = async (qrCode) => {
    try {
      setDownloadingId(qrCode.type);
      const token = localStorage.getItem('googleAuthToken');
      const response = await fetch(
        `${API}/admin/qr-codes/${qrCode.type}/download?size=500`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `owaza-qr-${qrCode.type.replace(/_/g, '-')}.svg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download QR code:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadAll = async () => {
    const enabledCodes = qrCodes.filter((qr) => qr.enabled);
    for (const qr of enabledCodes) {
      await handleDownload(qr);
      // Small delay between downloads
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  };

  const handleEdit = (qrCode) => {
    setEditingQr(qrCode);
    setEditForm({
      title: qrCode.title || '',
      description: qrCode.description || '',
      custom_url: qrCode.custom_url || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editingQr) return;

    try {
      setSaving(true);
      const response = await adminApi.put(`/api/admin/qr-codes/${editingQr.type}`, {
        title: editForm.title || undefined,
        description: editForm.description || undefined,
        custom_url: editForm.custom_url || null,
      });
      if (!response.ok) throw new Error('Failed to save');
      await loadQrCodes();
      setEditingQr(null);
    } catch (err) {
      console.error('Failed to save QR code:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={loadQrCodes} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="QR Code Center"
        description="Generate and manage QR codes for customer engagement"
        icon={<QrCode className="h-6 w-6" />}
        actions={
          <div className="flex gap-2">
            <Button onClick={loadQrCodes} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={handleDownloadAll} size="sm" variant="adminPrimary">
              <Package className="w-4 h-4 mr-2" />
              Download All
            </Button>
          </div>
        }
      />

      {/* Usage Tips */}
      <Card className="bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800">
        <CardContent className="p-4">
          <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">Print Tips</h3>
          <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
            <li>• Download QR codes at 500px size for print (business cards, flyers)</li>
            <li>• Place Review and Feedback QR codes near the front desk</li>
            <li>• Photo Upload QR is great for tournament events</li>
            <li>• vCard QR lets visitors save your contact info instantly</li>
          </ul>
        </CardContent>
      </Card>

      {/* QR Code Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {qrCodes.map((qrCode) => (
          <QRCodeCard
            key={qrCode.type}
            qrCode={qrCode}
            onToggle={handleToggle}
            onDownload={handleDownload}
            onEdit={handleEdit}
            onPrint={setPrintingQr}
            downloadingId={downloadingId}
          />
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingQr} onOpenChange={(open) => !open && setEditingQr(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Edit {editingQr && QR_TYPE_INFO[editingQr.type]?.name}
            </DialogTitle>
            <DialogDescription>
              Customize the title, description, or set a custom URL override.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Title</Label>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                placeholder="QR Code title..."
              />
            </div>

            <div>
              <Label>Description</Label>
              <Input
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="Brief description..."
              />
            </div>

            <div>
              <Label>Custom URL (optional)</Label>
              <Input
                value={editForm.custom_url}
                onChange={(e) => setEditForm({ ...editForm, custom_url: e.target.value })}
                placeholder="https://... (leave empty for default)"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Override the default URL this QR code points to
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingQr(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Card Dialog */}
      <PrintCardDialog
        qrCode={printingQr}
        open={!!printingQr}
        onClose={() => setPrintingQr(null)}
      />
    </div>
  );
};

export default QRCodeCenter;
